import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type {
  PromptOption,
  ApprovedTemplateOption,
} from '@/components/CampaignConfigForm'
import type { CampaignConfig, SellerListResponse } from '@sdr-agend/admin-schemas'

/**
 * Story 5.8 — Lead-new campaign config section.
 *
 * Rendered by `CampaignDetail.tsx` ONLY when `campaign.type === 'lead_new'`.
 * The base `CampaignConfigForm` covers cadence + templates + business_hours +
 * prompt_id. This section adds the lead-new-specific knobs:
 *
 *   - Prompt picker  → reuses `prompts` query (already in CampaignDetail)
 *   - HSM template picker (opening_template_name) → reuses `approvedTemplates`
 *   - Distribution rule → fixed `round_robin` for MVP
 *   - Critérios de desqualificação → free text consumed by Story 5.3 prompt
 *   - Seller pool → multiselect (active sellers; empty = all sellers)
 *   - Opening template vars → simple key/value JSON editor
 */
export interface LeadNewConfigSectionProps {
  value: CampaignConfig
  onChange: (next: CampaignConfig) => void
  prompts: ReadonlyArray<PromptOption>
  approvedTemplates: ReadonlyArray<ApprovedTemplateOption>
  disabled?: boolean
}

const DISTRIBUTION_RULE_OPTIONS = [
  { value: 'round_robin', label: 'Round-robin (atual)', disabled: false },
  { value: 'load_based', label: 'Por carga (em breve)', disabled: true },
  { value: 'specialty_based', label: 'Por especialidade (em breve)', disabled: true },
]

export function LeadNewConfigSection(
  props: LeadNewConfigSectionProps,
): React.ReactElement {
  const { value, onChange, prompts, approvedTemplates, disabled = false } = props

  // Fetch active sellers for the pool multiselect. Cheap query; cached by react-query.
  const { data: sellersData } = useQuery<SellerListResponse>({
    queryKey: ['admin-sellers', { active: true }],
    queryFn: async () => api<SellerListResponse>('/admin/sellers?active=true'),
  })
  const sellers = sellersData?.sellers ?? []

  function patch<K extends keyof CampaignConfig>(key: K, val: CampaignConfig[K]): void {
    onChange({ ...value, [key]: val })
  }

  function toggleSeller(id: string): void {
    const current = value.seller_pool ?? []
    const next = current.includes(id)
      ? current.filter((x: string) => x !== id)
      : [...current, id]
    patch('seller_pool', next.length > 0 ? next : undefined)
  }

  function updateOpeningVars(raw: string): void {
    try {
      const parsed = JSON.parse(raw)
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return
      const sanitized: Record<string, string> = {}
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'string') sanitized[k] = v
      }
      patch('opening_template_vars', Object.keys(sanitized).length > 0 ? sanitized : undefined)
    } catch {
      // Malformed JSON — caller decides; we keep last good value.
    }
  }

  const openingVarsString = JSON.stringify(value.opening_template_vars ?? {}, null, 2)

  return (
    <section className="grid gap-6 rounded-lg border bg-muted/20 p-5">
      <header>
        <h2 className="text-lg font-semibold">Configuração Lead Novo</h2>
        <p className="text-sm text-muted-foreground">
          Campos específicos do fluxo de aquisição de leads novos via GHL.
        </p>
      </header>

      {/* Prompt picker — reuses the base prompts list. */}
      <div className="grid gap-2">
        <Label htmlFor="ln-prompt">Prompt de qualificação</Label>
        <Select
          id="ln-prompt"
          value={value.prompt_id ?? ''}
          onChange={(e) => patch('prompt_id', e.target.value)}
          disabled={disabled || prompts.length === 0}
        >
          <option value="">— selecione —</option>
          {prompts.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </Select>
      </div>

      {/* HSM opening template. */}
      <div className="grid gap-2">
        <Label htmlFor="ln-opening">Template de abertura (HSM aprovado)</Label>
        <Select
          id="ln-opening"
          value={value.opening_template_name ?? ''}
          onChange={(e) =>
            patch('opening_template_name', e.target.value === '' ? undefined : e.target.value)
          }
          disabled={disabled || approvedTemplates.length === 0}
        >
          <option value="">— sem template (usa default `abertura_lead_novo`) —</option>
          {approvedTemplates.map((t) => (
            <option key={t.name} value={t.name}>
              {t.label}
            </option>
          ))}
        </Select>
      </div>

      {/* Distribution rule — MVP fixes to round_robin. */}
      <div className="grid gap-2 sm:max-w-sm">
        <Label htmlFor="ln-dist">Regra de distribuição</Label>
        <Select id="ln-dist" value="round_robin" disabled>
          {DISTRIBUTION_RULE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          MVP usa round-robin. Outras regras (carga, especialidade) virão em stories futuras.
        </p>
      </div>

      {/* Disqualification criteria. */}
      <div className="grid gap-2">
        <Label htmlFor="ln-criteria">Critérios de desqualificação</Label>
        <Textarea
          id="ln-criteria"
          value={value.disqualification_criteria ?? ''}
          onChange={(e) =>
            patch(
              'disqualification_criteria',
              e.target.value.length > 0 ? e.target.value : undefined,
            )
          }
          disabled={disabled}
          maxLength={2000}
          rows={4}
          placeholder="ex: orçamento abaixo de R$ 5k, fora da região, ainda não decidiu..."
        />
        <p className="text-xs text-muted-foreground">
          Interpolado no prompt como <code className="text-xs">{'{disqualification_criteria}'}</code>.
        </p>
      </div>

      {/* Seller pool multiselect. */}
      <div className="grid gap-2">
        <Label>Pool de vendedores</Label>
        {sellers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum vendedor ativo cadastrado.</p>
        ) : (
          <ul className="grid gap-2 max-h-60 overflow-y-auto rounded-md border bg-card/50 p-3">
            {sellers.map((s) => (
              <li key={s.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`seller-${s.id}`}
                  className="h-4 w-4 rounded border-input"
                  checked={(value.seller_pool ?? []).includes(s.id)}
                  onChange={() => toggleSeller(s.id)}
                  disabled={disabled}
                />
                <label htmlFor={`seller-${s.id}`} className="text-sm">
                  {s.name}
                </label>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">
          Vazio = todos os vendedores ativos elegíveis ao SellerDistributor.
        </p>
      </div>

      {/* Opening template variables — JSON. */}
      <div className="grid gap-2">
        <Label htmlFor="ln-vars">Variáveis do template (JSON)</Label>
        <Textarea
          id="ln-vars"
          value={openingVarsString}
          onChange={(e) => updateOpeningVars(e.target.value)}
          disabled={disabled}
          rows={4}
          spellCheck={false}
          placeholder={'{\n  "nome": "{{customer.name}}"\n}'}
        />
        <p className="text-xs text-muted-foreground">
          Map de placeholder → valor para construir os components do HSM.
        </p>
      </div>
    </section>
  )
}

export default LeadNewConfigSection
